import { Options } from 'scroll-into-view-if-needed';

export type CustomEasing = (t: number) => number;

export interface SmoothBehaviorOptions extends Options {
  behavior?: 'smooth';
  duration?: number;
  ease?: CustomEasing;
}

// Memoize so we're much more friendly to non-dom envs
let memoizedNow: () => number;
const now = () => {
  if (!memoizedNow) {
    memoizedNow =
      'performance' in window ? performance.now.bind(performance) : Date.now;
  }
  return memoizedNow();
};

type Context = {
  scrollable: Element;
  method: (x: number, y: number) => void;
  startTime: number;
  startX: number;
  startY: number;
  x: number;
  y: number;
  duration: number;
  ease: CustomEasing;
  cb: () => void;
};

function step(context: Context) {
  const time = now();
  const elapsed = Math.min((time - context.startTime) / context.duration, 1);
  // apply easing to elapsed time
  const value = context.ease(elapsed);

  const currentX = context.startX + (context.x - context.startX) * value;
  const currentY = context.startY + (context.y - context.startY) * value;

  context.method(currentX, currentY);

  // scroll more if we have not reached our destination
  if (currentX - context.x !== 0 || currentY - context.y !== 0) {
    requestAnimationFrame(() => step(context));
  } else {
    // If nothing left to scroll lets fire the callback
    context.cb();
  }
}

export function smoothScroll(
  el: Element,
  x: number,
  y: number,
  duration = 600,
  ease: CustomEasing = (t) => 1 + --t * t * t * t * t,
  cb: () => void,
) {
  // define scroll context
  const scrollable = el;
  const startX = el.scrollLeft;
  const startY = el.scrollTop;
  const method = (mx: number, my: number) => {
    // @TODO use Element.scroll if it exists, as it is potentially better performing
    el.scrollLeft = mx;
    el.scrollTop = my;
  };

  // scroll looping over a frame if needed
  step({
    scrollable,
    method,
    startTime: now(),
    startX,
    startY,
    x,
    y,
    duration,
    ease,
    cb,
  });
}
